"use client"
import { pickPostImageFormat } from "@/(admin)/modules/ui-builder/config/lib/post-helpers"
import PostLink from "@/components/PostLink"
import { toImageSrc } from "@/lib/utils"
import { PostData } from "@/types/types"
import clsx from "clsx"
import Image from "next/image"
import { formatRelative } from "../lib/time"
import type { Article } from "../types/new"

export function NewsItemCard({
  article,
  variant = "main",
  showTime = false,
  className,
  postData,
  priority = false,
}: {
  article: Article
  variant?: "main" | "small" | "sidebar" | "bullet"
  showTime?: boolean
  className?: string
  postData?: PostData
  priority?: boolean
}) {
  const { title, imageUrl, publishedAt } = article

  const getImageUrl = (format?: "thumbnail" | "small" | "medium" | "large" | "original") => {
    if (postData) {
      return pickPostImageFormat(postData, format) || imageUrl
    }
    return imageUrl
  }

  if (variant === "bullet") {
    return (
      <li className={clsx("flex items-start group", className)}>
        <span className="h-2 w-2 mt-[10px] mr-[10px] flex-shrink-0 bg-[#008746]" />
        <PostLink href={`/${article.slug}`} postId={postData?.id}>
          <span className="text-base leading-[1.4rem] font-normal text-black group-hover:text-[#1EAD46] duration-300 transition-colors">
            {title}
          </span>
        </PostLink>
      </li>
    )
  }

  return (
    <div className={className}>
      <PostLink href={`/${article.slug}`} postId={postData?.id} className={clsx("group block image-hover")}>
        {variant === "main" && getImageUrl("small") && (
          <div className={clsx("relative overflow-hidden mb-1 h-[198px] w-full")}>
            <Image
              src={toImageSrc(getImageUrl("small") ?? undefined)}
              alt={title}
              fill
              sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 280px"
              className="object-cover transition-transform duration-300"
              priority={priority}
              loading={priority ? undefined : "lazy"}
              quality={75}
            />
          </div>
        )}
        {variant === "main" && (
          <h3 className="text-base leading-[1.4rem] font-normal text-black group-hover:text-[#1EAD46] duration-300 transition-colors">
            {title}
          </h3>
        )}

        {variant === "small" && (
          <div className="flex gap-[10px] items-center">
            {getImageUrl("small") && (
              <div
                className={clsx(
                  "relative overflow-hidden flex-shrink-0 md:p-0 py-2 md:h-[87px] md:w-[84px] h-[70px]",
                  "sm:w-[23.5%] w-[40%]",
                )}
              >
                <Image
                  src={toImageSrc(getImageUrl("small") ?? undefined)}
                  alt={title}
                  fill
                  sizes="(max-width: 768px) 40vw, 84px"
                  className="object-cover transition-transform duration-300"
                  loading="lazy"
                  quality={75}
                />
              </div>
            )}
            <div className="flex-1 min-w-0 group md:p-0 py-2 sm:w-[76.5%] w-[60%]">
              {showTime && <p className="mt-1 text-xs">{formatRelative(publishedAt)}</p>}
              <h3 className="text-base leading-[1.4rem] font-normal text-black group-hover:text-[#1EAD46] duration-300 transition-colors ">
                {title}
              </h3>
            </div>
          </div>
        )}

        {variant === "sidebar" && (
          <>
            <div className="flex gap-4 items-stretch">
              {getImageUrl("small") && (
                <div className={clsx("relative overflow-hidden flex-shrink-0 h-[81px] w-[131px]")}>
                  <Image
                    src={toImageSrc(getImageUrl("small") ?? undefined)}
                    alt={title}
                    fill
                    sizes="(max-width: 640px) 44vw, 131px"
                    className="object-cover transition-transform duration-300"
                    loading="lazy"
                    quality={70}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0 group">
                <div className="flex items-center gap-[6px]">
                  <span className="h-2 w-2 flex-shrink-0 bg-[#008746]" />
                  {showTime && <p className="text-xs">{formatRelative(publishedAt)}</p>}
                </div>
                <h3 className="text-sm leading-[1.55] font-normal text-black group-hover:text-[#1EAD46] duration-300 transition-colors">
                  {title}
                </h3>
              </div>
            </div>
            <div className="border-b-[1.25px] border-dotted h-[1.25px] border-[#ced4da] mt-4 mb-5" />
          </>
        )}
      </PostLink>
    </div>
  )
}
